import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import ReactGridLayout, {
  Responsive as ResponsiveGridLayout,
} from 'react-grid-layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Permission } from '@/permissions/Permission';
import { Tooltip } from '@trevor/ui-components';
import { PreviewWidget } from '@/features/dashboard/components/PreviewWidget';
import { WidgetFilters } from '@/features/dashboard/components/WidgetFilters';
import HighlightFeature, {
  ACTION_CANCEL_DASHBOARD_CHANGES,
  ACTION_SAVE_DASHBOARD_CHANGES,
  TOP,
} from '@/components/HighlightFeature';
import { SaveBar } from '@/features/dashboard/components/SaveBar';
import { SIDEBAR_ANIMATION_FINISHED } from '@/constants/custom-event-names';
import { SelectedWidgetOverlay } from '@/features/dashboard/components/SelectedWidgetOverlay';
import { WidgetBottomControls } from '@/features/dashboard/components/WidgetBottomControls';
import styled from 'styled-components';
import {
  getDataTableForMetricsAndDimensions,
  getDataTableHeadingsForMetricsAndDimensions,
  validateDataTable,
} from '@/features/dashboard/utils';
import { userAction } from '@/util/helpers/ajax-helpers';
import { DASHBOARD_WIDGET_CONFIG_BUTTON } from '@/constants/test-ids';
import { TEXTBOX } from '@/components/ChartHolder.constants';
import { TABLE } from '@/components/charting/chartType.constants';
import { DashboardHeader } from '@/features/dashboard/components/DashboardHeader';
import { DashboardFilterBar } from '@/features/dashboard/components/DashboardFilterBar';
import {
  EMBEDDED_DASHBOARD,
  PUBLIC_DASHBOARD,
} from '@/features/dashboard/types';
import { EmbeddedDownloadCsv } from '@/features/embeddedDashboards/EmbeddedDownloadCsv';
import ChartHolder from './ChartHolder';
import AjaxSpinner from './AjaxSpinner';
import buttons from '../css/module/buttons.css';
import flex from '../css/module/flex.css';
import style from '../css/module/dashboard-filters.css';
import css from '../css/module/dashboard.css';
import MultiDropdown from './MultiDropdown';
import DropdownButton from './DropdownButton';
import DropdownItem from '../util/DropdownItem';
import ErrorBoundary from './ErrorBoundary';
import { TextBoxWidget } from './TextBoxWidget';

const DashboardBody = styled.div`
  margin-bottom: 50px;
  position: relative;
  padding-bottom: 100px;
  padding-top: ${(p) => (p.withTopPadding ? '58px' : '0')};
`;

/**
 * Uses: https://www.npmjs.com/package/react-grid-layout
 */
class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = this.dashboardDims();
    this.dashboardRoot = React.createRef();
  }

  updateDimensions = () => {
    if (this.mounted) {
      this.setState(this.dashboardDims());
    }
  };

  dashboardDims() {
    const width = !this.isFullScreen()
      ? $(window).width() - 60
      : $(window).width();
    return {
      width,
    };
  }

  rerender = () => {
    this.forceUpdate();
  };

  componentDidMount() {
    const { onAwaitingWidgetData } = this.props;

    this.mounted = true;
    this.updateDimensions();
    window.addEventListener(SIDEBAR_ANIMATION_FINISHED, this.rerender);
    window.addEventListener('resize', this.updateDimensions);
    // while dashboard is open - regularly poll for results that are still outstanding
    const pollServer = () => {
      this.timer = setTimeout(() => {
        if (this.mounted) {
          if (this.props.outstandingWidgetRequests < 1) {
            onAwaitingWidgetData(true);
          }
          pollServer();
        }
      }, 1000);
    };

    pollServer();
  }

  isFullScreen() {
    if (
      document.fullscreen ||
      document.mozFullScreen ||
      document.msFullscreen ||
      document.webkitFullscreen
    ) {
      return true;
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    clearTimeout(this.timer);
    window.removeEventListener(SIDEBAR_ANIMATION_FINISHED, this.rerender);
    window.removeEventListener('resize', this.updateDimensions);
  }

  buildWidgetContent(
    chartConfig,
    dt,
    error,
    displayType,
    widget,
    index,
    userInteraction,
    dashboardId,
    datasourceId,
  ) {
    if (displayType === 'preview') {
      return <PreviewWidget index={index} widget={widget} />;
    }

    if (displayType === TEXTBOX) {
      return (
        <TextBoxWidget
          widget={widget}
          isInEditMode={this.props.state.textBoxesInEditMode[index]}
          index={index}
          userInteraction={userInteraction}
          onUpdate={(textBoxRef) =>
            userInteraction({
              section: 'update-textbox',
              newRef: textBoxRef,
              widgetIndex: widget.key,
            })()
          }
          onExitEditMode={userInteraction({
            section: 'toggle-textbox-edit-mode',
            widgetIndex: index,
            value: false,
          })}
        />
      );
    }
    if (!dt) {
      if (error) {
        return (
          <div>
            <div className="alert alert-danger" role="alert">
              <b>Problem loading saved query:</b>
              <br />
              <br />
              {error}
            </div>
            <Permission
              permissionPath={`${datasourceId}.dashboards.${dashboardId}.write`}
            >
              {this.isNotEditable() ? null : (
                <div className="margin-left-20">
                  Click{' '}
                  <Link
                    to={`/datasources/${datasourceId}/saved-queries/${widget.series}`}
                  >
                    here
                  </Link>{' '}
                  to open in Query builder.
                  <br />
                  <br /> Or if you're thoroughly baffled,{' '}
                  <a
                    href="#"
                    className="no-wrap"
                    onClick={(e) => {
                      e.preventDefault();
                      Intercom('show');
                    }}
                  >
                    contact us
                  </a>
                  , we're happy to help.
                </div>
              )}
            </Permission>
          </div>
        );
      }
      return (
        <div className={flex.middle} style={{ marginTop: '-20px' }}>
          <AjaxSpinner small />
        </div>
      );
    }
    if (dt.rows().length === 0) {
      return (
        <div
          className={flex.middle}
          style={{ marginTop: '-20px', fontSize: '1.2em' }}
        >
          <span className="faint">0 results</span>
        </div>
      );
    }

    // custom settings always have priority
    const chartSetting = widget.chartSetting || chartConfig;
    const newColumns = (widget.dimensionColumns || []).concat(
      widget.metricColumns || [],
    );
    const headings = getDataTableHeadingsForMetricsAndDimensions(
      dt,
      newColumns,
    );
    const isValid = validateDataTable(headings, displayType);
    const configuredDataTable = isValid
      ? getDataTableForMetricsAndDimensions(dt, newColumns)
      : dt;
    return (
      <ChartHolder
        presentation={isValid ? displayType : TABLE}
        dataTable={configuredDataTable}
        chartingOptions={chartSetting}
        ignoreChartRules
        isDashboard
      />
    );
  }

  getWidgetFilters(filters, series) {
    const array = filters.filter((f) => f.series().includes(series));
    return array;
  }

  appliedFilter(i, isApplied, wf) {
    return (
      <div
        key={i}
        style={{ fontSize: '0.9em' }}
        className={`${buttons.tag} ${isApplied ? style.filterApplied : ''}`}
      >
        <span className={`${buttons.tagIcon} ${style.filterTag}`}>
          <FontAwesomeIcon icon="filter" />
        </span>
        <span className={buttons.tagContent}>{wf.name()}</span>
      </div>
    );
  }

  // show filter clause in tooltip.
  getClause(name, appliedFilters) {
    // find the applied filter whose lhs matches the name of the widget filter name.
    const filter = appliedFilters.find((af) => af.lhs().name() === name);
    const clause = filter.buildContent(() => null, null, true);
    return clause;
  }

  dashboardBody(
    userInteraction,
    chartWidgets,
    textWidgets,
    previewWidget,
    views,
    chartConfigs,
    dataTables,
    errors,
    onOpenViewInNewTab,
    onOpenViewWithFilters,
    onConfigureChart,
  ) {
    const widgetChanged = (widget, ly) => {
      const z = ly.findIndex((l) => l.i === widget.key);
      if (z === -1) {
        return true;
      }
      const layoutToCheck = ly[z];
      return (
        widget.x !== layoutToCheck.x ||
        widget.y !== layoutToCheck.y ||
        widget.height !== layoutToCheck.h ||
        widget.width !== layoutToCheck.w
      );
    };
    const onLayoutChange = (layout) => {
      // check if anything has changed and return out if not. Need to do this because, otherwise, it tries to update every time you click the widget settings dropdown.
      const change =
        chartWidgets.some((widget) => widgetChanged(widget, layout)) ||
        textWidgets.some((widget) => widgetChanged(widget, layout));
      if (!change || layout.length === 0) {
        return;
      }
      userInteraction({ section: 'layout', layout })();
    };
    const { filters } = this.props.state;
    const { dashboardId, datasourceId, widgetBeingEdited } = this.props;
    const { appliedFilters } = this.props.state;
    let widgetWithViewIndex = 0; // Using this index to access the view that belongs to a non textbox widget
    const gridItem = (widget, index) => {
      const series = widget.displayType === TEXTBOX ? null : widget.series;
      const view =
        widget.displayType === TEXTBOX
          ? views[index]
          : views[widgetWithViewIndex++];
      const widgetFilters =
        filters.length > 0 && widget.displayType !== TEXTBOX
          ? this.getWidgetFilters(filters, series)
          : null;
      // The following tables only contain information if widget is not text box
      const dt = dataTables[index];
      const error = errors[index];
      const chartConfig = chartConfigs[index];

      const content = this.buildWidgetContent(
        chartConfig,
        dt,
        error,
        widget.displayType,
        widget,
        index,
        userInteraction,
        dashboardId,
        datasourceId,
      );

      const onDeleteWidget = () => {
        return userInteraction({
          section: 'deleteWidget',
          widgetIndex: widget.key,
          displayType: widget.displayType,
        });
      };

      const filtersApplied =
        widgetFilters &&
        appliedFilters.filter((f) =>
          widgetFilters.some((wf) => f.lhs().name() === wf.name()),
        );

      const widgetTitle = widget.widgetTitle || view?.name();
      const showWidgetBottomControls =
        index === widgetBeingEdited && widget.displayType !== TEXTBOX;

      return (
        <div
          key={widget.key}
          data-grid={{
            x: widget.x,
            y: widget.y,
            w: widget.width,
            h: widget.height,
            minW: 12,
            minH: 12,
          }}
          className={`${css.widget} widget ${
            widget.displayType === TEXTBOX
              ? css.textBox
              : index === widgetBeingEdited
              ? css.editedWidget
              : ''
          }`}
        >
          {widget.displayType !== TEXTBOX && (
            <EmbeddedDownloadCsv
              fileName={widgetTitle}
              widget={widget}
              dashboardType={this.props.dashboardType}
              dataTables={this.props.state.dataTables}
            />
          )}

          <div className={flex.column} style={{ height: '100%' }}>
            <div
              className={`widget-header ${flex.alignItemsStart} ${flex.justifyContentBetween}`}
            >
              <div
                className={`${flex.row} ${flex.wrap} widget-title-container`}
              >
                {widget.displayType === TEXTBOX ? null : (
                  <h5
                    data-tooltip-id={`fullName_${widget.key}`}
                    className="widget-title"
                  >
                    {widgetTitle}
                    <Tooltip
                      className="fullNameTooltip"
                      id={`fullName_${widget.key}`}
                      place="bottom"
                    >
                      {widgetTitle}
                    </Tooltip>
                  </h5>
                )}
                {widgetFilters && !this.props.state.public && (
                  <WidgetFilters widgetIndex={index} />
                )}
              </div>
              {widget.displayType === 'preview' ||
              this.isNotEditable() ||
              this.isFullScreen() ? null : (
                <Permission
                  permissionPath={`${datasourceId}.dashboards.${dashboardId}.write`}
                >
                  <div className={css.settings}>
                    <DropdownButton
                      contentWidth={185}
                      contentBuilder={() => (
                        <MultiDropdown
                          items={[
                            widget.displayType === TEXTBOX &&
                            !this.props.state.textBoxesInEditMode[index]
                              ? DropdownItem.dropdown(
                                  'Edit text',
                                  userInteraction({
                                    section: 'toggle-textbox-edit-mode',
                                    widgetIndex: index,
                                    value: true,
                                  }),
                                )
                              : null,
                            widget.displayType === TEXTBOX
                              ? null
                              : DropdownItem.dropdown(
                                  'Open in Query builder',
                                  onOpenViewInNewTab(view),
                                ),
                            widget.displayType !== TEXTBOX &&
                            appliedFilters.length &&
                            filtersApplied?.length
                              ? DropdownItem.dropdown(
                                  'Open in Query builder with filters applied',
                                  onOpenViewWithFilters(
                                    view,
                                    filtersApplied,
                                    this.props.state.active.id(),
                                  ),
                                )
                              : null,
                            widget.displayType === TEXTBOX
                              ? null
                              : DropdownItem.dropdown(
                                  'Configure chart',
                                  onConfigureChart(
                                    view,
                                    widget,
                                    dt,
                                    chartConfig,
                                    index,
                                  ),
                                ),
                            DropdownItem.dropdown(
                              'Remove',
                              onDeleteWidget(index),
                            ),
                          ]}
                          className={style.widgetDropdown}
                        />
                      )}
                    >
                      <div
                        className={style.widgetDropdownIcon}
                        data-testid={DASHBOARD_WIDGET_CONFIG_BUTTON}
                      >
                        <FontAwesomeIcon icon="cog" />
                      </div>
                    </DropdownButton>
                  </div>
                </Permission>
              )}
            </div>
            <div className={`${flex.content} widget-body notDraggable`}>
              <ErrorBoundary>{content}</ErrorBoundary>
            </div>
          </div>
          {showWidgetBottomControls && (
            <WidgetBottomControls
              isPreview={widget.isPreview}
              dashboardId={dashboardId}
            />
          )}
        </div>
      );
    };
    const gridItems = [
      ...textWidgets.map(gridItem),
      ...chartWidgets.map(gridItem),
    ].concat(previewWidget ? gridItem(previewWidget, chartWidgets.length) : []);
    const LayoutComponent = this.isNotEditable()
      ? ResponsiveGridLayout
      : ReactGridLayout;
    const COLS = this.isNotEditable()
      ? { lg: 96, md: 96, sm: 16, xs: 16, xxs: 16 }
      : 96;

    const isDraggable = !this.isNotEditable() && this.state.width > 450; // disabled on small viewports (i.e. mobile) to avoid unintentionally moving the widgets while trying to scroll
    const layoutWidth = !this.isFullScreen()
      ? this.dashboardRoot.current?.offsetWidth ?? 0
      : $(window).width(); // taken from the existing logic dashboardDims()

    return (
      <LayoutComponent
        className="layout"
        width={layoutWidth}
        draggableCancel=".notDraggable"
        isResizable={!this.isNotEditable()}
        isDraggable={isDraggable}
        margin={[10, 10]}
        cols={COLS}
        rowHeight={1}
        onLayoutChange={onLayoutChange}
      >
        {gridItems}
      </LayoutComponent>
    );
  }

  isPublicDash = () => this.props.dashboardType === PUBLIC_DASHBOARD; // true if we've rendered a public dashboard

  isEmbedded = () => this.props.dashboardType === EMBEDDED_DASHBOARD;

  isNotEditable = () => this.isPublicDash() || this.isEmbedded();

  render() {
    const {
      state,
      userInteraction,
      restricted,
      onSave,
      onDiscard,
      onOpenViewInNewTab,
      onOpenViewWithFilters,
      onRefreshClicked,
      onConfigureChart,
      dashboardId,
      datasourceId,
      dashboardType,
    } = this.props;

    const body =
      !state.chartWidgets && !state.textWidgets ? (
        <div className={`${flex.middle} ${flex.middleOfVP}`}>
          <AjaxSpinner />
        </div>
      ) : (
        this.dashboardBody(
          userInteraction,
          state.chartWidgets,
          state.textWidgets,
          state.previewWidget,
          state.views,
          state.chartConfigs,
          state.dataTables,
          state.errors,
          onOpenViewInNewTab,
          onOpenViewWithFilters,
          onConfigureChart,
        )
      );

    const onFullScreen = () => {
      const elem = document.getElementById('dashboardBody');
      const fullScreen =
        elem.requestFullScreen ||
        elem.webkitRequestFullScreen ||
        elem.mozRequestFullScreen;
      fullScreen.call(elem);
    };
    const bottomBarClicked =
      (action, isCancel = false) =>
      (e) => {
        const actionType = isCancel
          ? ACTION_CANCEL_DASHBOARD_CHANGES
          : ACTION_SAVE_DASHBOARD_CHANGES;
        action(e);
        userAction(actionType, { dashboard_id: dashboardId });
      };

    const updateDashboardFilters = (filters) => {
      const filtersBefore = state.appliedFilters;
      const filtersAfter = filters;
      userInteraction({ section: 'applied-filters', filters })();
      onRefreshClicked(null, filtersBefore, filtersAfter);
    };

    const dashboard = state.active;

    return (
      <div
        className="dashboard"
        style={{ position: 'relative' }}
        ref={this.dashboardRoot}
      >
        <DashboardHeader
          onFullScreen={onFullScreen}
          width={this.state.width}
          userInteraction={userInteraction}
          dashboardType={dashboardType}
          updateDashboardFilters={updateDashboardFilters}
        />
        <DashboardBody id="dashboardBody" withTopPadding={this.isPublicDash()}>
          {!restricted ? null : (
            <div className="dashboard-limitation">
              Dashboards are only available to teams of 3 or more
            </div>
          )}
          {this.isFullScreen() ? null : (
            <DashboardFilterBar
              dashboardType={dashboardType}
              updateDashboardFilters={updateDashboardFilters}
            />
          )}
          {body}
          <SelectedWidgetOverlay />
        </DashboardBody>

        {state.unsavedChanges && (
          <Permission
            permissionPath={`${datasourceId}.dashboards.${dashboardId}.write`}
          >
            <SaveBar
              dashboardId={dashboardId}
              saveId="dashboard-save-changes"
              className="position-fixed-bottom"
              onSave={bottomBarClicked(onSave)}
              onCancel={bottomBarClicked(onDiscard(dashboard), true)}
            >
              <HighlightFeature
                position={TOP}
                text={
                  <span>
                    You've made changes. Click the buttons below to{' '}
                    <b>save or discard</b> those changes.
                  </span>
                }
                targetAction={[
                  ACTION_SAVE_DASHBOARD_CHANGES,
                  ACTION_CANCEL_DASHBOARD_CHANGES,
                ]}
                style={{ minWidth: '290px' }}
              />
            </SaveBar>
          </Permission>
        )}
      </div>
    );
  }
}

export default Dashboard;
